import {
	CodeOutlined,
	DownloadOutlined,
	FileExcelOutlined,
	FileImageOutlined,
	FileOutlined,
	FilePdfOutlined,
	FileTextOutlined,
	FileZipOutlined,
} from '@ant-design/icons';
import * as React from 'react';
import { useState } from 'react';
import OWButton from '../../../components/ow_button/OWButton';
import { classifyAgentError } from '../../../utils/AgentErrorUtils';
import { cn } from '../../../utils/ClassNamesUtils';

interface OscarFileCardProps {
	name: string;
	/** Bytes, straight from the `file` stream event. */
	size: number;
	/** Absolute harness url — `useAgentChat` has already resolved it. */
	url: string;
	/**
	 * Fetches the bytes. Injected rather than imported so the transport stays in
	 * the thunks layer and this stays a presentational card.
	 */
	onDownload?: (url: string) => Promise<Blob>;
	className?: string;
}

/** What kind of thing this is, keyed by extension. */
const FILE_KINDS: Array<{ label: string; icon: React.ReactNode; extensions: string[] }> = [
	{
		label: 'Spreadsheet',
		icon: <FileExcelOutlined />,
		extensions: ['csv', 'tsv', 'xls', 'xlsx'],
	},
	{
		label: 'Image',
		icon: <FileImageOutlined />,
		extensions: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'],
	},
	{ label: 'Document', icon: <FilePdfOutlined />, extensions: ['pdf'] },
	{ label: 'Archive', icon: <FileZipOutlined />, extensions: ['zip', 'tar', 'gz'] },
	{
		label: 'Code',
		icon: <CodeOutlined />,
		extensions: ['json', 'js', 'jsx', 'ts', 'tsx', 'css', 'html', 'py', 'sh', 'sql', 'yml', 'yaml'],
	},
	{ label: 'Text', icon: <FileTextOutlined />, extensions: ['txt', 'md', 'log'] },
];

const extensionOf = (name: string): string => {
	const dot = (name || '').lastIndexOf('.');
	return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
};

const kindFor = (extension: string) => {
	const match = FILE_KINDS.filter((kind) => kind.extensions.indexOf(extension) !== -1)[0];
	return match || { label: 'File', icon: <FileOutlined />, extensions: [] };
};

/**
 * Bytes, honestly. The old line divided by 1024 and clamped to a minimum of 1,
 * so a 200-byte note reported "1 KB".
 */
const formatSize = (bytes: number): string => {
	if (!bytes || bytes < 0) {
		return '';
	}
	if (bytes < 1024) {
		return `${bytes} B`;
	}
	if (bytes < 1024 * 1024) {
		return `${Math.round(bytes / 1024)} KB`;
	}
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * A file the agent produced, as something you can see rather than a link in a
 * line of prose. It used to render as an underlined filename with a 12px glyph,
 * which read as a footnote — but a generated export is usually the whole point
 * of asking, and it is the one thing in a turn the user takes away with them.
 *
 * The download fetches the bytes rather than pointing a link at the harness.
 * The link would mostly work — these are served as
 * `Content-Disposition: attachment` — but on an expired session the browser
 * would navigate the tab to a JSON error and take the app with it. Fetching
 * keeps the failure here, next to the file it belongs to.
 */
export const OscarFileCard = ({ name, size, url, onDownload, className }: OscarFileCardProps) => {
	const [saving, setSaving] = useState(false);
	const [failure, setFailure] = useState('');
	const extension = extensionOf(name);
	const kind = kindFor(extension);
	const details = [kind.label, extension ? extension.toUpperCase() : '', formatSize(size)]
		.filter(Boolean)
		.join(' · ');

	const handleDownload = async () => {
		if (!onDownload) {
			return;
		}
		setSaving(true);
		setFailure('');
		try {
			const blob = await onDownload(url);
			// A blob url is same-origin, so `download` is honoured and the file
			// keeps its name — a cross-origin href would have the attribute ignored.
			const objectUrl = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = objectUrl;
			link.download = name;
			document.body.appendChild(link);
			link.click();
			link.remove();
			// Released on the next tick: revoking synchronously cancels the save in
			// Safari, which reads the blob after the click returns.
			window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
		} catch (downloadError) {
			setFailure(classifyAgentError(downloadError).message);
		} finally {
			setSaving(false);
		}
	};

	return (
		<div
			className={cn(
				'flex w-full min-w-0 items-center gap-s rounded border border-solid border-grey-4 bg-grey-2 p-xs',
				className
			)}
		>
			<span
				aria-hidden="true"
				className="flex size-10 shrink-0 items-center justify-center rounded border border-solid border-grey-4 bg-white text-[18px] text-character-secondary"
			>
				{kind.icon}
			</span>
			<span className="flex min-w-0 flex-1 flex-col">
				<span className="bodymedium truncate text-character-primary" title={name}>
					{name}
				</span>
				<span
					className={cn(
						'footnoteregular',
						failure ? 'text-character-danger' : 'text-character-secondary'
					)}
				>
					{failure || details}
				</span>
			</span>
			<OWButton
				htmlType="button"
				size="large"
				icon={<DownloadOutlined />}
				loading={saving}
				onClick={handleDownload}
				className="shrink-0"
			>
				{failure ? 'Retry' : 'Download'}
			</OWButton>
		</div>
	);
};

export default OscarFileCard;
